/**
 * Arena geometry — Sprint 7.
 *
 * Canonical AABB list for the Sprint 2 arena. Consumed by:
 *   - server `GrenadeSystem` for grenade-wall collision + HE LOS
 *   - client `predictTrajectory` for the throw preview arc (must match
 *     the server's authoritative flight)
 *
 * The client also has `apps/fps/src/scene/Arena.ts` that owns the
 * *rendering* side (tints, materials, ramps, lighting). Its AABB list
 * was previously the source of truth — kept in sync manually with this
 * module. If gameplay-affecting geometry changes, update **both** until
 * the render side migrates to consume this module directly.
 */
import { ARENA_LAYOUT } from "./arenaLayout";
import { ARENA_PROPS_DATA } from "./arena.props.data";

// Surface label on AABBs. The render side (apps/fps Arena) adds textured
// variants (wall/pillar/midfloor); they're allowed here so its collision
// boxes stay assignable to ArenaAABB. Server logic treats them as inert.
export type SurfaceMaterial =
  | "concrete" | "wood" | "metal" | "dirt"
  | "wall" | "pillar" | "midfloor" | "glass" | "woodwall";

export interface ArenaAABB {
  minX: number; maxX: number;
  minY: number; maxY: number;
  minZ: number; maxZ: number;
  material?: SurfaceMaterial;
}

/**
 * One collision box of a prop, defined in the model's LOCAL space BEFORE
 * yaw: a slab from the floor (y = 0) up to `top`, centered at `(cx, cz)`
 * with footprint `sx × sz`. {@link propColliders} yaw-rotates it into a
 * world AABB. Stacking a few of these with rising `top` lets a tall prop
 * be *climbed* — each vertical step stays under `MOVEMENT_FPS.STEP_HEIGHT`
 * so the player auto-mantles up instead of being shoved aside like a wall.
 */
export interface PropCollider {
  cx: number; cz: number;
  sx: number; sz: number;
  /** Box top height (metres above the floor); base is always y = 0. */
  top: number;
}

/**
 * A static prop rendered as a 3D model on the client (apps/fps) but
 * collided against here as plain yaw-aligned AABBs — so the server
 * (movement, bullets, grenades) and the client's prediction agree on
 * where it is. The render side imports the same {@link ARENA_PROPS}
 * list and places each model at `(x, y, z)` with a `yawDeg` rotation, so
 * the collision boxes and the visible model can never drift apart.
 */
export interface ArenaProp {
  /** Stable id (entity name suffix on the render side). */
  id: string;
  /** URL of the GLB loaded by the client renderer. */
  modelUrl: string;
  /**
   * World position of the model origin — its footprint center, on the floor
   * (`y = 0`) unless the prop is stacked on top of another (then `y` is the
   * height of its base, and its collision box is lifted to match).
   */
  x: number; y: number; z: number;
  /** Y rotation in degrees applied to the model and every collider. */
  yawDeg: number;
  /** Model-local footprint BEFORE yaw/scale — extent along model X/Y/Z (metres). */
  sizeX: number; sizeY: number; sizeZ: number;
  /**
   * Uniform scale applied to BOTH the rendered model and its collision —
   * the footprint plus every {@link PropCollider}'s offsets and height.
   * Default 1. `sizeX/Y/Z` and `colliders` are the model's NATIVE (pre-scale)
   * dimensions; scaling multiplies them.
   */
  scale?: number;
  material?: SurfaceMaterial;
  /**
   * Optional stepped collision profile (local space). When omitted the
   * prop collides as one full `sizeX × sizeY × sizeZ` footprint box.
   */
  colliders?: PropCollider[];
}

/**
 * Downloaded models (Poly Haven, CC0) dropped into the arena, placed at the
 * spots the user laid out on a top-down sketch.
 *
 * `covered_car` (native ~1.79 × 1.41 × 4.38 m) is turned 90° (`yawDeg: 0`,
 * length along Z) and scaled 1.35×, parked NORTH of the mid platform — pulled
 * a bit further off it (z −11) — to block the north lane.
 *
 * Its collision is a stepped profile along the car's length (local Z): cabin
 * (full height) flanked by lower hood/trunk and bumpers. Every step (after
 * the 1.35× scale) still rises ≤ STEP_HEIGHT (0.7 m), so a player mantles up
 * bumper → hood → roof from either end instead of being walled out; the cabin
 * gives full-height cover.
 *
 * The two cover models are scattered to their marked spots:
 *   • three `barrel`s ("Barrel_01" explosive) at 2× scale, stacked in a
 *     pyramid, in the north-east lane (≈ x 6.5, z −14.5);
 *   • two big `barricade`s ("Concrete Road Barrier") at 3×, both turned
 *     north–south to sit across the lanes — one south-west (≈ −7.5, 9) and
 *     one south-east (≈ 6.5, 15).
 * Every prop collides through the scaled footprint boxes derived below.
 */
// Live prop list — sourced from the editable data file (read + written by the
// map editor). The covered_car default + its stepped collider profile live
// there; new props the editor adds get a single measured footprint box.
export const ARENA_PROPS: readonly ArenaProp[] = ARENA_PROPS_DATA;

// LEGACY (removed in arena redesign — kept for reference):
// The three scatter barrels (2× pyramid in the NE lane) and the two 3×
// concrete road barriers (turned N–S across the lanes) were dropped when
// the arena was rebuilt around the centered lockers / hollow T room / gate
// walls. Preserved here per the "preserve legacy code" rule.
//
//  // ── Cover scattered to the user's marked spots ─────────────────────────
//  // Three barrels at 2× scale stacked in a pyramid, in the north-east lane.
//  // Base pair now runs along Z (north–south) so the stack sits ACROSS the
//  // east–west lane and blocks it, with one barrel on top.
//  {
//    id: "barrel_1",
//    modelUrl: "/models/props/barrel.glb",
//    x: 6.5, y: 0, z: -15.08,
//    yawDeg: 0,
//    scale: 2,
//    sizeX: 0.563, sizeY: 0.88, sizeZ: 0.563,
//    material: "metal",
//  },
//  {
//    id: "barrel_2",
//    modelUrl: "/models/props/barrel.glb",
//    x: 6.5, y: 0, z: -13.92,
//    yawDeg: 0,
//    scale: 2,
//    sizeX: 0.563, sizeY: 0.88, sizeZ: 0.563,
//    material: "metal",
//  },
//  {
//    id: "barrel_3",
//    modelUrl: "/models/props/barrel.glb",
//    // Top of the pyramid — base lifted onto the two below (0.88 × 2 = 1.76 m).
//    x: 6.5, y: 1.76, z: -14.5,
//    yawDeg: 0,
//    scale: 2,
//    sizeX: 0.563, sizeY: 0.88, sizeZ: 0.563,
//    material: "metal",
//  },
//
//  // Two concrete road barriers at 3× (≈4.6 m long, 2.5 m tall), both turned
//  // north–south (yawDeg 90, length along Z) so they sit ACROSS the east–west
//  // lanes and block them: one south-west, one further south-east.
//  {
//    id: "barricade_1",
//    modelUrl: "/models/props/barricade.glb",
//    x: -7.5, y: 0, z: 9.0,
//    yawDeg: 90,
//    scale: 3,
//    sizeX: 1.545, sizeY: 0.831, sizeZ: 0.639,
//    material: "concrete",
//  },
//  {
//    id: "barricade_2",
//    modelUrl: "/models/props/barricade.glb",
//    x: 6.5, y: 0, z: 15.0,
//    yawDeg: 90,
//    scale: 3,
//    sizeX: 1.545, sizeY: 0.831, sizeZ: 0.639,
//    material: "concrete",
//  },

// The static-box geometry (perimeter, lockers, pillars, platform, crates,
// gates, corridors, window-walls, dog-door) now lives in `arenaLayout.ts` as
// the single data-driven `ARENA_LAYOUT`. The old imperative constants +
// generators are preserved in `legacy/arenaGeometry.imperative.legacy.ts`.

function box(centerX: number, y: number, centerZ: number, w: number, h: number, d: number, material?: SurfaceMaterial): ArenaAABB {
  return {
    minX: centerX - w / 2,
    maxX: centerX + w / 2,
    minY: y - h / 2,
    maxY: y + h / 2,
    minZ: centerZ - d / 2,
    maxZ: centerZ + d / 2,
    material,
  };
}

/**
 * Convert one local {@link PropCollider} into a world AABB, applying the
 * prop's yaw. Collision stays axis-aligned, so only quarter-turns are
 * supported (enough for parked props): a 90°/270° turn rotates the local
 * (cx, cz) center and swaps the footprint's X/Z extents. The box sits on
 * the floor — center Y = top / 2, height = top.
 */
function colliderToAabb(p: ArenaProp, c: PropCollider): ArenaAABB {
  // Apply the prop's uniform scale to every local dimension + offset.
  const s = p.scale ?? 1;
  const cx = c.cx * s, cz = c.cz * s, sx = c.sx * s, sz = c.sz * s, top = c.top * s;
  const quarter = (((Math.round(p.yawDeg / 90) % 4) + 4) % 4);
  let wx: number, wz: number, ww: number, wd: number;
  switch (quarter) {
    case 1:  wx = p.x + cz; wz = p.z - cx; ww = sz; wd = sx; break;
    case 2:  wx = p.x - cx; wz = p.z - cz; ww = sx; wd = sz; break;
    case 3:  wx = p.x - cz; wz = p.z + cx; ww = sz; wd = sx; break;
    default: wx = p.x + cx; wz = p.z + cz; ww = sx; wd = sz; break;
  }
  // Box base sits at the prop's `y` (0 on the floor, or the top of whatever
  // it's stacked on); height is the scaled `top`.
  return box(wx, p.y + top / 2, wz, ww, top, wd, p.material ?? "metal");
}

/**
 * All world collision boxes for a prop: its explicit stepped `colliders`
 * if given, else a single full-footprint box.
 */
function propToAabbs(p: ArenaProp): ArenaAABB[] {
  const cols = p.colliders ?? [{ cx: 0, cz: 0, sx: p.sizeX, sz: p.sizeZ, top: p.sizeY }];
  return cols.map((c) => colliderToAabb(p, c));
}

let cachedAabbs: ArenaAABB[] | null = null;

/**
 * Return the full list of static AABBs for the arena. Cached after the
 * first call. Includes the perimeter walls + ceiling so grenades stay
 * inside the playable volume.
 */
export function getArenaAABBs(): readonly ArenaAABB[] {
  if (cachedAabbs) return cachedAabbs;
  const out: ArenaAABB[] = [];

  // Static boxes — perimeter, lockers, pillars/T-room, mid platform, crates,
  // gates, corridors, window-walls, dog-door — all derive from the single
  // data-driven ARENA_LAYOUT, so the render side (apps/fps Arena) and the
  // in-browser map editor share exactly this geometry. The arena is open to
  // the sky (no ceiling box); grenades stay in via the perimeter walls +
  // GrenadeSystem's XZ clamp (height-independent).
  for (const lb of ARENA_LAYOUT) {
    out.push({
      minX: lb.cx - lb.w / 2, maxX: lb.cx + lb.w / 2,
      minY: lb.cy - lb.h / 2, maxY: lb.cy + lb.h / 2,
      minZ: lb.cz - lb.d / 2, maxZ: lb.cz + lb.d / 2,
      material: lb.surface,
    });
  }

  // Downloaded static props (covered car, …) — same definitions the
  // client renders as 3D models, collided here as (optionally stepped)
  // AABBs so player movement, bullets, and grenades all stop at them.
  for (const prop of ARENA_PROPS) out.push(...propToAabbs(prop));

  cachedAabbs = out;
  return out;
}

/**
 * Breakable glass pane — fills the T-pillar room's EAST window opening
 * (the y1.45..4.0 gap between the E-window sill + lintel), centered in z
 * between the two `troom-e-post-*` mullions that frame it. While intact it
 * blocks bullets + grenade/flash LINE-OF-SIGHT, the player body (added to
 * the movement push-out list via `arenaAabbsWithGlass`/the client's
 * FirstPersonController), and thrown/dropped weapons; it breaks on the
 * first bullet, HE blast, or knife swing that reaches it, after which all
 * of those pass through. It is NOT a `getSurfaceY` floor (nobody stands on
 * top of it). The render side (apps/fps Arena) builds a box byte-identical
 * to this AABB.
 */
export const GLASS_PANE_AABB: ArenaAABB = box(-7.332, 2.725, 0, 0.8, 2.55, 4.4, "glass");

// Module-level precomputed (lazy-init, cached once) bullet/LOS AABB lists
// for the two glass states. NEVER mutate `cachedAabbs` — these spread it
// into fresh arrays so the static movement/surface list stays glass-free.
let cachedAabbsGlassIntact: ReadonlyArray<ArenaAABB> | null = null;
let cachedAabbsGlassBroken: ReadonlyArray<ArenaAABB> | null = null;

/**
 * AABB list for the current glass state. `intact` adds the
 * {@link GLASS_PANE_AABB} on top of {@link getArenaAABBs}; `broken` is just
 * the base list. Used for bullet wall-occlusion, grenade bounce/HE LOS,
 * flash LOS, AND the player body push-out (so the intact pane blocks the
 * body and passes once broken). NOT used for `getSurfaceY` floor support —
 * the pane is a wall, not a standable surface.
 */
export function arenaAabbsWithGlass(broken: boolean): ReadonlyArray<ArenaAABB> {
  if (broken) {
    if (!cachedAabbsGlassBroken) cachedAabbsGlassBroken = getArenaAABBs();
    return cachedAabbsGlassBroken;
  }
  if (!cachedAabbsGlassIntact) cachedAabbsGlassIntact = [...getArenaAABBs(), GLASS_PANE_AABB];
  return cachedAabbsGlassIntact;
}

/**
 * Inclined walkable surface. The player capsule's XZ center is tested
 * inside [minX,maxX]×[minZ,maxZ]; the surface Y at that XZ is linearly
 * interpolated along X between `yAtMinX` and `yAtMaxX`.
 */
export interface ArenaRamp {
  minX: number; maxX: number;
  minZ: number; maxZ: number;
  yAtMinX: number;
  yAtMaxX: number;
}

const STAIR_RUN = 6;
const STAIR_W = 3;
const T_STAIR_X_START = -32;
const CT_STAIR_X_START = +32;

let cachedRamps: ArenaRamp[] | null = null;

/**
 * Stair ramps connecting the floor to the two catwalks (Sprint 2).
 * Mirrored in `apps/fps/src/scene/Arena.ts` (MAP_RAMPS) — keep in sync
 * until the render side migrates to consume this module directly.
 */
export function getArenaRamps(): readonly ArenaRamp[] {
  if (cachedRamps) return cachedRamps;
  // Corner stairs to the catwalk were removed — no ramps in the arena now.
  cachedRamps = [];
  return cachedRamps;
}

/**
 * Highest walkable surface Y at world position `(x, z)` for a player
 * capsule of `radius`, taking `currentY` into account so the underside
 * of a slab isn't treated as a floor while jumping up through it.
 *
 * Consults both inclined ramps (point-in-rect test, linear Y between
 * the two X edges) and AABB tops (XZ capsule footprint overlap, top
 * face counts only when the player is at or near it — `currentY >=
 * box.maxY - 0.05`). Defaults the world floor to `floor` (0 in this
 * arena).
 *
 * Identical algorithm to the client's `FirstPersonController.getSurfaceY`.
 * Server uses this so authoritative `player.y` matches the height the
 * player visually stands at — pre-fix the server clamped everyone to
 * `FLOOR_Y` and shot rays fired from the floor regardless of crates /
 * catwalks the player was standing on. (Bullet went visibly below the
 * crosshair from up on cover.)
 */
export function getSurfaceY(
  x: number,
  z: number,
  currentY: number,
  radius: number,
  aabbs: ReadonlyArray<ArenaAABB>,
  ramps: ReadonlyArray<ArenaRamp>,
  floor: number = 0,
): number {
  let surfaceY = floor;

  for (let i = 0; i < ramps.length; i++) {
    const r = ramps[i];
    if (x < r.minX || x > r.maxX) continue;
    if (z < r.minZ || z > r.maxZ) continue;
    const t = (x - r.minX) / (r.maxX - r.minX);
    const y = r.yAtMinX + t * (r.yAtMaxX - r.yAtMinX);
    if (y > surfaceY) surfaceY = y;
  }

  for (let i = 0; i < aabbs.length; i++) {
    const b = aabbs[i];
    if (x + radius < b.minX || x - radius > b.maxX) continue;
    if (z + radius < b.minZ || z - radius > b.maxZ) continue;
    if (currentY < b.maxY - 0.05) continue;
    if (b.maxY > surfaceY) surfaceY = b.maxY;
  }

  return surfaceY;
}

/**
 * Ray vs AABB list — returns the smallest positive t where the ray
 * enters any box, along with the surface normal at the entry point.
 * Used by GrenadeSystem for wall collision + HE LOS attenuation.
 *
 * tMax limits the ray length. Returns null if no hit within tMax.
 */
export function rayHitAabb(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  tMax: number,
  boxes: ReadonlyArray<ArenaAABB>,
): { t: number; nx: number; ny: number; nz: number; box: ArenaAABB } | null {
  let bestT = tMax;
  let bestNx = 0, bestNy = 1, bestNz = 0;
  let bestBox: ArenaAABB | null = null;

  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i];
    let tNear = -Infinity;
    let tFar = Infinity;
    let nearAxis = 0;
    let nearSign = 1;

    // Slab method, axis by axis.
    const slab = (oC: number, dC: number, lo: number, hi: number, axis: number): boolean => {
      if (Math.abs(dC) < 1e-9) {
        return oC >= lo && oC <= hi;
      }
      let t0 = (lo - oC) / dC;
      let t1 = (hi - oC) / dC;
      let sign = -1;
      if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; sign = 1; }
      if (t0 > tNear) { tNear = t0; nearAxis = axis; nearSign = sign; }
      if (t1 < tFar) tFar = t1;
      return tNear <= tFar;
    };

    if (!slab(ox, dx, b.minX, b.maxX, 0)) continue;
    if (!slab(oy, dy, b.minY, b.maxY, 1)) continue;
    if (!slab(oz, dz, b.minZ, b.maxZ, 2)) continue;
    if (tNear < 0 || tNear > tFar) continue;
    if (tNear >= bestT) continue;

    bestT = tNear;
    bestNx = nearAxis === 0 ? nearSign : 0;
    bestNy = nearAxis === 1 ? nearSign : 0;
    bestNz = nearAxis === 2 ? nearSign : 0;
    bestBox = b;
  }

  if (!bestBox) return null;
  return { t: bestT, nx: bestNx, ny: bestNy, nz: bestNz, box: bestBox };
}

/**
 * True if a player capsule (square XZ footprint of half-extent `radius`,
 * body from `feetY` to `feetY + height`) could stand at `(x, z, feetY)`
 * without its body intersecting any box — i.e. there is headroom. Boxes
 * whose top is at or below the feet are walkable ground and never block.
 *
 * Used by the step-up logic so we never mantle a player UP into an
 * overhang / wall they wouldn't fit under.
 */
function canStandAt(
  x: number,
  feetY: number,
  z: number,
  height: number,
  radius: number,
  boxes: ReadonlyArray<ArenaAABB>,
): boolean {
  const headY = feetY + height;
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i];
    // Footprint overlap (square capsule vs AABB in XZ).
    if (x + radius <= b.minX || x - radius >= b.maxX) continue;
    if (z + radius <= b.minZ || z - radius >= b.maxZ) continue;
    // Box entirely at/below the feet → it's ground we'd stand on, not a
    // blocker.
    if (b.maxY - 0.05 <= feetY) continue;
    // The body would pierce this box's vertical extent → no headroom.
    if (b.minY < headY) return false;
  }
  return true;
}

/**
 * Resolve a player capsule (square XZ footprint of half-extent `radius`,
 * feet at `pos.y`, head at `pos.y + height`) against a list of AABBs.
 * Mutates `pos`. May also raise `pos.y` when it auto-steps the player
 * onto a low ledge (see `stepHeight`). Returns `{ steppedUp }` so the
 * caller can cancel downward velocity after a mantle.
 *
 * Identical algorithm on client (`FirstPersonController.resolveCollisions`)
 * and server (`FpsArena.tick`) — kept in shared so they stay in
 * lockstep. Without parity the authoritative position walks through
 * pillars and the client's `reconcileFromServer` yanks the player into
 * geometry every snapshot ("sticks against the mid pillar / W moves
 * sideways").
 *
 * Per box, in priority order:
 *  1. Skip boxes the player already stands ON (feet ≥ top − ε).
 *  2. Skip boxes with no vertical overlap.
 *  3. Skip boxes the footprint doesn't overlap in XZ.
 *  4. **Step-up / mantle:** if the box top is at most `stepHeight` above
 *     the feet AND the player would fit standing on it, lift the player
 *     onto the top instead of pushing sideways. This is what makes
 *     jumping onto crates / the mid platform reliable instead of
 *     wedging the player against (or inside) the block.
 *  5. Otherwise push out along the smaller-overlap axis (slide along the
 *     longer face).
 *
 * Up to 4 iterations to settle corners / stacked ledges.
 */
export function resolveAabbCollisions(
  pos: { x: number; y: number; z: number },
  height: number,
  radius: number,
  boxes: ReadonlyArray<ArenaAABB>,
  stepHeight = 0,
): { steppedUp: boolean } {
  let steppedUp = false;

  for (let iter = 0; iter < 4; iter++) {
    let anyHit = false;

    for (let i = 0; i < boxes.length; i++) {
      const box = boxes[i];

      // (1) Standing on top of this box — its sides shouldn't push.
      if (pos.y >= box.maxY - 0.05) continue;

      // (2) No Y overlap (jumped clear above or fully below).
      if (pos.y + height <= box.minY || pos.y >= box.maxY) continue;

      // (3) XZ footprint overlap.
      const ox = Math.min(pos.x + radius, box.maxX) - Math.max(pos.x - radius, box.minX);
      const oz = Math.min(pos.z + radius, box.maxZ) - Math.max(pos.z - radius, box.minZ);
      if (ox <= 0 || oz <= 0) continue;

      // (4) Step-up / mantle onto a low ledge instead of being blocked.
      if (stepHeight > 0) {
        const climb = box.maxY - pos.y;
        if (
          climb > 0 &&
          climb <= stepHeight &&
          canStandAt(pos.x, box.maxY, pos.z, height, radius, boxes)
        ) {
          pos.y = box.maxY;
          steppedUp = true;
          anyHit = true;
          continue; // box is now underfoot — no lateral push.
        }
      }

      // (5) Lateral push-out (slide along the longer face).
      anyHit = true;
      if (ox < oz) {
        pos.x += pos.x < (box.minX + box.maxX) * 0.5 ? -ox : ox;
      } else {
        pos.z += pos.z < (box.minZ + box.maxZ) * 0.5 ? -oz : oz;
      }
    }

    if (!anyHit) break;
  }

  return { steppedUp };
}
